/**
 * הרשאות לחברות — granting a person selected companies. D5 / CG-1..CG-7, OA-0001 §3.7 / §3.8.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THIS IS THE SECURITY SCREEN. THE ROLES SCREEN IS NOT.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Three tables, three capabilities, three screens, and only the middle one is access control:
 *
 *   role      → what a user may DO                  `core.role_permission`
 *   grant     → which companies they may do it ON   `core.user_company_access`   ← THIS SCREEN
 *   portfolio → whose dashboard it appears on       `core.company_portfolio_assignment`
 *
 * The third one is the trap, and OA-0001 §3.8 rules on it: PORTFOLIO IS NOT SECURITY. Assigning a
 * company to one partner changes what they SEE FIRST and cannot change what anybody may OPEN — the
 * portfolio table has no reader in the authorization path at all, and a guardrail names it so it
 * cannot acquire one. Legacy's counter-example is a hardcoded four-name staff list that AUTO-GRANTED
 * companies, with the names already drifted from the live store.
 *
 * ── WHAT THE RULES MAKE THIS SCREEN LOOK LIKE ───────────────────────────────────────────────────
 *
 *   CG-2  There is no `deny` level, so there is no "block" control here. EXCLUSION IS THE ABSENCE OF
 *         A ROW. To exclude a company from a person, do not grant it — including from the owner of
 *         the office, and including for conflict of interest.
 *   CG-4  Choosing five companies writes FIVE INDIVIDUALLY REVOCABLE ROWS, never one bulk flag. The
 *         list below is a set of checkboxes for exactly that reason.
 *   CG-6  Revoking one removes one (person, company) pair and nothing else.
 *   CG-7  A revocation carries a NAMED reason code, because "conflict of interest" is a recorded
 *         classification with a column, not a note in an audit row.
 *   D-12  Legacy's console wrote `assigned_users` and NO AUTHORIZATION PATH READ IT: the screen
 *         reported success and access did not change. The table behind this screen has exactly one
 *         reader — the gate — and a guardrail asserts that reader exists.
 *
 * ── AND WHY THERE IS NO DROPDOWN ────────────────────────────────────────────────────────────────
 *
 * 09 §3.4: company and period are chosen in the context bar and nowhere else, and
 * `shell/contextSingleSource.test.ts` scans the source tree to keep it that way. The companies here
 * are not a CONTEXT choice — they are the subject of an action — so they are checkboxes on a form,
 * which is also the shape CG-4 wants.
 */

import { useCallback, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { AccessLevel, CompanyGrantSummary } from '../api/contract';
import { accessLevelHe, formatInstantHe } from '../api/adminVocabulary';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { ServerRead } from '../components/ServerRead';
import { bothOf, listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'הרשאות לחברות';

const LEVELS: readonly { readonly code: AccessLevel; readonly hintHe: string }[] = [
  { code: 'read', hintHe: 'צפייה בנתוני החברה בלבד.' },
  { code: 'write', hintHe: 'צפייה ועדכון נתונים בחברה.' },
  { code: 'approve', hintHe: 'צפייה, עדכון ואישור פעולות בחברה.' },
];

type Outcome =
  | { readonly kind: 'granted'; readonly rows: number }
  | { readonly kind: 'revoked'; readonly companyUid: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function TenantCompanyAccessScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [membershipUid, setMembershipUid] = useState('');
  const [chosenCompanyUids, setChosenCompanyUids] = useState<readonly string[]>([]);
  const [level, setLevel] = useState<AccessLevel>('read');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * BOTH AXES, READ SEPARATELY. **Phase 2 §6, vertical A.**
   *
   * `tenant.member.read` gives the people to grant to; `tenant.grant.read` gives what is granted
   * already. Both are required to render this screen honestly — a grant table with no rows and a
   * grant table that could not be read are opposite statements about whether a colleague can open a
   * client's file, and 01 §A5's whole point is that the ABSENCE of a grant IS the exclusion.
   *
   * The COMPANIES come from the session context rather than a third read: `ctx.visibleCompanies` is
   * already the set this session may see, and re-reading it here would be a second answer to a
   * question the shell has asked once.
   */
  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readGrants = useCallback(() => callOperation('tenant.grant.read', {}), []);
  const membersRead = useServerData(readMembers);
  const grantsRead = useServerData(readGrants);

  const members = listOf(membersRead.result, 'members');
  const grants = listOf(grantsRead.result, 'grants');
  const companies = ctx.visibleCompanies;

  const companyNames = useMemo(
    () => new Map(companies.map((c) => [c.companyUid, c.legalName])),
    [companies],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  const rowsForMember = membershipUid === '' ? grants : grants.filter((g) => g.membershipUid === membershipUid);

  function toggleCompanyUid(companyUid: string): void {
    setChosenCompanyUids((current) =>
      current.includes(companyUid)
        ? current.filter((uid) => uid !== companyUid)
        : [...current, companyUid],
    );
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      // CG-4: N companies, N rows. The route takes an array for that reason, not for convenience.
      const result = await callOperation('tenant.grant.create', {
        body: { membershipUid, companyUids: chosenCompanyUids, accessLevel: level },
      });
      setOutcome({ kind: 'granted', rows: result.granted });
      setChosenCompanyUids([]);
      // RE-READ. The table below is the office's record of who can open which client's file, and a
      // locally-patched copy of it is the last thing in this product that should drift.
      grantsRead.reload();
    } catch (err: unknown) {
      // Granting company access requires step-up (04 §2.3). The gate says so through the ordinary
      // denial channel — `stepup_required` has its own Hebrew — and a handler-side step-up carries
      // `need` and is NOT a denial. `classifyActionError` keeps the two apart.
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  /**
   * ONE ROW. The route takes the pair and nothing wider, so there is no request shape here that could
   * remove a person's access to everything at once — which is what makes the exclusion model
   * reversible without making it coarse.
   */
  async function revoke(grant: CompanyGrantSummary): Promise<void> {
    setPending(true);
    setOutcome(null);
    try {
      await callOperation('tenant.grant.revoke', {
        body: { membershipUid: grant.membershipUid, companyUid: grant.companyUid },
      });
      setOutcome({ kind: 'revoked', companyUid: grant.companyUid });
      grantsRead.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  const grantColumns: readonly Column<CompanyGrantSummary>[] = [
    {
      key: 'company',
      headerHe: 'החברה',
      render: (g) => companyNames.get(g.companyUid) ?? 'חברה שאינה זמינה לך',
    },
    {
      key: 'person',
      headerHe: 'המשתמש',
      render: (g) =>
        members.find((m) => m.membershipUid === g.membershipUid)?.displayName ?? 'משתמש שאינו זמין',
    },
    { key: 'level', headerHe: 'רמת הגישה', render: (g) => accessLevelHe(g.accessLevel) },
    {
      key: 'granted',
      headerHe: 'הוענקה',
      render: (g) => <span dir="ltr">{formatInstantHe(g.grantedAtUtc) ?? '—'}</span>,
    },
    {
      key: 'expires',
      headerHe: 'בתוקף עד',
      render: (g) => (
        <span dir="ltr">{formatInstantHe(g.expiresAtUtc) ?? 'ללא מועד סיום'}</span>
      ),
    },
    {
      key: 'revoke',
      headerHe: 'הסרה',
      /*
        CG-6 AS A CONTROL: removing one row changes exactly one (person, company) pair.
        A grant could be CREATED and never removed, which made every mistake permanent and made
        conflict-of-interest exclusion — D5's explicit requirement — a thing an office could enter and
        not leave. There is deliberately no "revoke all" button beside it: bulk provisioning writes N
        INDIVIDUALLY REVOCABLE rows (CG-4), and a control that removed them together would be the
        "all companies" flag the model does not have, arriving through the back door.
      */
      render: (g) => (
        <button
          type="button"
          className="btn btn--quiet"
          disabled={pending}
          onClick={() => void revoke(g)}
        >
          הסרת הגישה
        </button>
      ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'ניהול המשרד', href: routes.tenantAdmin }}
      provenanceHe="גישה לחברה היא הענקה מפורשת לכל חברה בנפרד. חברה שלא הוענקה אינה נגישה — גם לבעלים של המשרד."
    >
      <section className="notice" data-tone="info">
        <h2 className="notice__title">אין רשימת חסימה</h2>
        <p className="notice__body">
          כדי למנוע ממשתמש גישה לחברה — אין צורך לחסום אותה. פשוט לא מעניקים אותה. החרגה היא היעדר
          הרשאה, וכך אפשר לשמור על הפרדה בשל ניגוד עניינים בלי להסתמך על סימון נוסף.
        </p>
      </section>

      <ServerRead
        result={bothOf(membersRead.result, grantsRead.result)}
        whatHe="את המשתמשים ואת ההרשאות"
      >
        {() => (
          <>
            <form className="form" onSubmit={(e) => void submit(e)}>
              <fieldset className="field field--choice">
                <legend className="field__label">המשתמש</legend>
                {members.map((m) => (
                  <label key={m.membershipUid} className="choice">
                    <input
                      type="radio"
                      name="membership"
                      value={m.membershipUid}
                      checked={membershipUid === m.membershipUid}
                      onChange={() => setMembershipUid(m.membershipUid)}
                    />
                    <span>
                      {m.displayName}
                      <span className="choice__hint" dir="ltr">
                        {m.email}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="field field--choice">
                <legend className="field__label">החברות שיוענקו</legend>
                {companies.map((c) => (
                  <label key={c.companyUid} className="choice">
                    <input
                      type="checkbox"
                      name="companyUids"
                      value={c.companyUid}
                      checked={chosenCompanyUids.includes(c.companyUid)}
                      onChange={() => toggleCompanyUid(c.companyUid)}
                    />
                    <span>{c.legalName}</span>
                  </label>
                ))}
                <span className="field__hint">
                  כל חברה שתסומן נרשמת כהרשאה נפרדת, וניתן לבטל כל אחת מהן בנפרד מבלי לגעת באחרות.
                </span>
              </fieldset>

              <fieldset className="field field--choice">
                <legend className="field__label">רמת הגישה</legend>
                {LEVELS.map((l) => (
                  <label key={l.code} className="choice">
                    <input
                      type="radio"
                      name="accessLevel"
                      value={l.code}
                      checked={level === l.code}
                      onChange={() => setLevel(l.code)}
                    />
                    <span>
                      {accessLevelHe(l.code)}
                      <span className="choice__hint">{l.hintHe}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="actions-row">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={pending || membershipUid === '' || chosenCompanyUids.length === 0}
                >
                  {pending ? 'שולח…' : `הענקת גישה ל־${chosenCompanyUids.length} חברות`}
                </button>
              </div>
              <p className="form__note">
                הענקת גישה היא פעולה רגישה. ייתכן שתידרש הזדהות נוספת לפני שהיא תתבצע, והיא נרשמת ביומן
                הביקורת בכל מקרה.
              </p>
            </form>

            {outcome === null ? null : outcome.kind === 'granted' ? (
              <StateBlock
                titleHe="הגישה הוענקה"
                bodyHe={`נרשמו ${outcome.rows} הרשאות נפרדות, אחת לכל חברה.`}
              />
            ) : outcome.kind === 'revoked' ? (
              <StateBlock
                titleHe="ההרשאה בוטלה"
                // CG-6: one row, one pair. Nobody else lost anything, and the sentence says so.
                bodyHe={`בוטלה הרשאה אחת — לחברה אחת ולמשתמש אחד. שאר ההרשאות של אותו משתמש לא השתנו.`}
              />
            ) : (
              // One classification, the same one every screen uses. The step-up that granting may require
              // is NOT a denial, and `<ActionFailureNotice>` is what keeps the two apart.
              <ActionFailureNotice failure={outcome.failure} />
            )}

            <DataTable<CompanyGrantSummary>
              captionHe="הרשאות קיימות"
              columns={grantColumns}
              rows={rowsForMember}
              rowKey={(g) => `${g.membershipUid}:${g.companyUid}`}
              filterSummaryHe={
                membershipUid === ''
                  ? `מציג ${grants.length} הרשאות — כל ההרשאות במשרד`
                  : `מציג ${rowsForMember.length} מתוך ${grants.length} הרשאות · לפי המשתמש שנבחר`
              }
              emptyStateHe="לא נרשמו הרשאות לחברות."
            />
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}
